export interface ColumnPresentation {
  key: string
  show?: boolean
  width?: number
  sticky?: number | string | boolean
  [key: string]: any
}

export type ColumnPresentationChange = "visibility" | "sticky"

const STICKY_COLUMN_ENDPOINTS = new Set([
  "gefahrstoffe",
  "rechtsgrundlagen",
  "pflichten",
  "tasks",
])

interface MergeColumnPresentationOptions {
  preferSpecWidth?: boolean
  forceSpecVisibility?: boolean
}

export const MIN_COLUMN_WIDTH = 50

export function columnCssVariableName(key: unknown, suffix: string) {
  const token = Array.from(String(key), (character) => {
    return /^[A-Za-z0-9]$/.test(character)
      ? character
      : `_${character.codePointAt(0)!.toString(16)}_`
  }).join("") || "empty"

  return `--col-${token}-${suffix}`
}

export function columnChangeRequiresDataReload(change: ColumnPresentationChange) {
  return change === "visibility"
}

export function supportsStickyColumns(endpoint?: string | null) {
  return STICKY_COLUMN_ENDPOINTS.has(String(endpoint || ""))
}

export function mergeColumnsWithPresentation(
  specColumns: ColumnPresentation[],
  savedColumns: ColumnPresentation[],
  options: MergeColumnPresentationOptions = {},
) {
  const specByKey = new Map(specColumns.map((column) => [column.key, column]))
  const savedKeys = new Set(savedColumns.map((column) => column.key))
  const merged: ColumnPresentation[] = []

  for (const savedColumn of savedColumns) {
    const specColumn = specByKey.get(savedColumn.key)
    if (!specColumn) continue

    const savedHasWidth = Object.prototype.hasOwnProperty.call(savedColumn, "width")
    const specHasWidth = Object.prototype.hasOwnProperty.call(specColumn, "width")
    merged.push({
      ...specColumn,
      ...savedColumn,
      label: specColumn.label,
      type: specColumn.type,
      options: specColumn.options,
      display_options: specColumn.display_options,
      required: specColumn.required,
      write: specColumn.write,
      disabled: specColumn.disabled,
      disabledReason: specColumn.disabledReason,
      optionsEndpoint: specColumn.optionsEndpoint,
      conditional_filter: specColumn.conditional_filter,
      show: options.forceSpecVisibility
        ? specColumn.show !== false
        : (Object.prototype.hasOwnProperty.call(savedColumn, "show") ? savedColumn.show : specColumn.show !== false),
      width: options.preferSpecWidth && specHasWidth
        ? specColumn.width
        : (savedHasWidth ? savedColumn.width : (specColumn.width ?? 150)),
      sticky: Object.prototype.hasOwnProperty.call(savedColumn, "sticky")
        ? savedColumn.sticky
        : (specColumn.sticky ?? 0),
    })
  }

  for (const specColumn of specColumns) {
    if (savedKeys.has(specColumn.key)) continue
    merged.push({
      ...specColumn,
      show: specColumn.show !== false,
      width: specColumn.width ?? 150,
      sticky: specColumn.sticky ?? 0,
    })
  }

  return merged
}

export function setColumnWidth(columns: ColumnPresentation[], key: string, width: number) {
  const column = columns.find((candidate) => String(candidate.key) === String(key))
  if (!column) return false

  column.width = Math.max(MIN_COLUMN_WIDTH, width)
  return true
}

export function effectiveColumnWidth(
  column: ColumnPresentation,
  overrides: Record<string, number> = {},
) {
  const override = overrides[String(column.key)]
  const width = Number(override ?? column.width ?? 150)
  return Math.max(MIN_COLUMN_WIDTH, Number.isFinite(width) ? width : 150)
}

export function stickyColumnCssVariables(
  columns: ColumnPresentation[],
  leftByKey: Record<string, number>,
  enabled: boolean,
) {
  const style: Record<string, string> = {}

  for (const column of columns) {
    const key = String(column.key)
    const isSticky = enabled && column.show !== false && Number(column.sticky || 0) > 0
    style[columnCssVariableName(key, "sticky-position")] = isSticky ? "sticky" : "static"
    style[columnCssVariableName(key, "sticky-left")] = isSticky ? `${leftByKey[key] || 0}px` : "auto"
    style[columnCssVariableName(key, "sticky-z-index")] = isSticky ? "998" : "auto"
    style[columnCssVariableName(key, "sticky-background-opacity")] = isSticky ? "1" : "0"
  }

  return style
}
